import { useState, useEffect, useMemo } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { JiraIcon } from "@atlaskit/logo-next";

import {
    JiraFFLContentGenerationResponse,
    useBxpWebStargateClient
} from "@/libraries/bxp-web-stargate-client/src";
import { wacToken } from "@/libraries/design/foundations";
import { messages } from "@/templates/paid-landing/sections/cfind-4666-frictionless-first-look/messages.ts";
import { useLocation } from "@/utils/location-provider";

import { formatCgsData } from "../../utils/paid-jira-ai-ffl";

import { Background } from "./paid-jira-ai-preview-modal/background";
import { PreviewTable } from "./paid-jira-ai-preview-modal/preview-table";

const backgroundCss = css({
    position: "fixed",
    inset: 0,
    width: "100vw",
    height: "100vh",
    zIndex: 10,
    background: wacToken("color.neutral.light0"),
    pointerEvents: "none", // Prevent interaction
    padding: "20px 28px"
});

const mainContainerCss = css({
    display: "flex",
    flexDirection: "column",
    height: "100vh"
});

const mainTopCss = css({
    alignSelf: "flex-start"
});

const mainBottomCss = css({
    flexGrow: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
});

const previewTableCss = css({
    width: "100%"
});

const modalOverlayCss = css({
    position: "fixed",
    inset: 0,
    width: "100vw",
    height: "100vh",
    zIndex: 30,
    // eslint-disable-next-line local/ensure-wac-token-usage
    background: "rgba(30, 41, 59, 0.3)",
    pointerEvents: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
});

export const PaidJiraAiPreviewBackground = ({
    children
}: {
    children: React.ReactNode;
}) => {
    const stargateClient = useBxpWebStargateClient();
    const intl = useIntl();
    const location = useLocation();
    const [cgsDataLoading, setCgsDataLoading] = useState(false);
    const [cgsData, setCgsData] = useState<
        JiraFFLContentGenerationResponse | undefined
    >(undefined);

    const formattedCgsData = useMemo(() => {
        if (cgsDataLoading || !cgsData) {
            return undefined;
        }

        return formatCgsData(cgsData);
    }, [cgsData, cgsDataLoading]);

    const searchParameters = new URLSearchParams(location.search);
    const cgsKey = searchParameters.get("cgsKey") as string | undefined;
    useEffect(() => {
        const fetchCgsData = async (cgsKey: string) => {
            try {
                setCgsDataLoading(true);
                const response = await stargateClient.getJiraFFLContentByCgsKey(
                    cgsKey
                );
                setCgsData(response);
                setCgsDataLoading(false);
            } catch (error) {
                setCgsDataLoading(false);
                console.error("Error fetching CGS data:", error);
            }
        };

        if (cgsKey) {
            fetchCgsData(cgsKey);
        }
    }, [cgsKey, stargateClient]);

    // Disable scrolling
    useEffect(() => {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, []);

    return (
        <>
            <div css={backgroundCss} className="modal-background">
                <Background />
                <div css={mainContainerCss}>
                    <div css={mainTopCss}>
                        <JiraIcon appearance="brand" shouldUseNewLogoDesign />
                    </div>
                    <div css={mainBottomCss}>
                        <div css={previewTableCss}>
                            <PreviewTable
                                rows={cgsDataLoading ? [] : formattedCgsData}
                                spaceName={
                                    cgsDataLoading
                                        ? undefined
                                        : cgsData?.sampleIssues.spaceName
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div
                css={modalOverlayCss}
                className="modal-overlay"
                role="dialog"
                aria-modal="true"
                aria-label={intl.formatMessage(messages.paidJiraAiPreviewModal)}
            >
                {children}
            </div>
        </>
    );
};
